import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { map, takeUntil, catchError } from 'rxjs/operators';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Passenger } from '@shared/services/passenger-change/_interfaces_/passenger-change.interface';
import { EventEmitter } from '@angular/core';
import { GenderEnum } from '@enums/gender.enum';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';
import { PassengerChangeFormKeysEnum } from '@enums/passengerChangeFormKeys.enum';
import { ContentService } from '@shared/services/content/content.service';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { PassengerChangeContent } from '@shared/services/content/_interfaces_/content.interface';
import { AlertMessageContentMapped } from '@shared/services/content/_interfaces_/content.mapped.interface';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-passenger-accordion',
  templateUrl: './passenger-accordion.component.html',
  styleUrls: ['./passenger-accordion.component.scss'],
})
export class PassengerAccordionComponent implements OnDestroy, OnInit {
  @Input() passenger: Passenger;
  @Input() index: number;
  @Input() isOpen: boolean;
  @Input() group: FormGroup;
  @Output() switchEvent = new EventEmitter<{ state: boolean; index: number }>();
  @Output() inputUpdateEvent = new EventEmitter();
  private unsubscribe$ = new Subject();
  public genders: GenderEnum[] = [GenderEnum.Male, GenderEnum.Female];
  private initialFormValue: FormGroup;
  public nameChangeInfo$: Observable<AlertMessageContentMapped>;

  constructor(
    private contentService: ContentService,
    private contentMapperService: ContentMapperService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    const { wasPassengerChanged, ...basePassengerDetails } = this.group.value;
    this.initialFormValue = basePassengerDetails;
    this.checkChangeAvailability();
    this.subscribeToInputChanges();
    this.retrieveNameChangeInfo();
  }

  private retrieveNameChangeInfo(): void {
    const defaultContent = { body: '' };
    this.nameChangeInfo$ = this.translateService.onLangChange.pipe(
      startWith(null),
      switchMap(() => this.contentService.getContent<PassengerChangeContent>(ContentTypesEnum.passengerChange)),
      map((content: PassengerChangeContent) => {
        if (!content || !content.nameChangeInfoText) {
          return defaultContent;
        }
        return this.contentMapperService.mapAlertMessage(content.nameChangeInfoText);
      }),
      catchError((error) => {
        return of(defaultContent);
      }),
      takeUntil(this.unsubscribe$)
    );
  }

  private checkChangeAvailability(): void {
    if (!this.passenger.dateOfBirthChangeAvailable) {
      this.group.get(PassengerChangeFormKeysEnum.dateOfBirth)?.disable();
    }
    if (!this.passenger.nameChangeAvailable) {
      this.group.get(PassengerChangeFormKeysEnum.firstName)?.disable();
      this.group.get(PassengerChangeFormKeysEnum.lastName)?.disable();
    }
    if (!this.passenger.genderChangeAvailable) {
      this.group.get(PassengerChangeFormKeysEnum.gender)?.disable();
    }
  }

  private getWasPassengerChangedControl(): AbstractControl {
    return this.group.get(PassengerChangeFormKeysEnum.wasPassengerChanged);
  }

  public onSwitchChange($event: any): void {
    this.isOpen = $event.target.checked;
    if (!this.isOpen) {
      this.resetFormGroup();
    }
    this.switchEvent.emit({ state: $event.target.checked, index: this.index });
  }

  isEnabled(): boolean {
    return this.passenger?.nameChangeAvailable || this.passenger?.genderChangeAvailable || this.passenger?.dateOfBirthChangeAvailable;
  }

  private resetFormGroup(): void {
    this.group.reset(this.initialFormValue, { emitEvent: false });
    this.getWasPassengerChangedControl().patchValue(false, { emitEvent: false });
  }

  private subscribeToInputChanges(): void {
    this.group.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      const { wasPassengerChanged, ...basePassengerDetails } = this.group.getRawValue();
      const hasDataChanged = !this.areValuesEqual(this.initialFormValue, basePassengerDetails);
      this.getWasPassengerChangedControl().patchValue(hasDataChanged, { emitEvent: false });
      this.inputUpdateEvent.emit(hasDataChanged);
    });
  }

  private areValuesEqual(initialValues: FormGroup, endValues: FormGroup): boolean {
    return JSON.stringify(initialValues) === JSON.stringify(endValues);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}
